// External
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { bool, object, func } from 'prop-types';
import { Formik, Form } from 'formik';
import { Box, Flex } from 'rebass';
import {
  ButtonStyled,
  ModalStyled,
  ModalActionsStyled,
  TextFieldStyled,
  DropDownStyled
} from '@nike/nike-design-system-components';

// Components
import Page from '../common/Page';
import Spinner from '../../components/spinner/Spinner';
import LinkedCatalogsAndAccounts from './linkedCatalogsAndAccounts/LinkedCatalogsAndAccounts';

// Functions
import { getPendingStatus } from '../../util/requestReducer';
import { fetchCatalogTypes, fetchCatalogTypeById, updateCatalogType, deleteCatalogType } from './actions';
import { getCatalogTypeById } from './selectors';
import useModal from '../../hooks/useModal';

// Constants
import { ERROR_MESSAGES, validationSchema } from './constants';
import { SALES_ORGS } from '../catalogs/constants';

const EditCatalogType = ({
  deleteCatalogType,
  fetchCatalogTypeById,
  fetchCatalogTypes,
  history,
  isUpdating,
  isFetching,
  updateCatalogType,
  selectedCatalogType
}) => {
  const [isModalOpen, openModal, closeModal] = useModal();

  useEffect(() => {
    fetchCatalogTypes();
  }, []);

  const handleSave = async (values, { setSubmitting, resetForm }) => {
    const catalogTypeId = values?.id;
    const catalogTypeData = {
      name: values.name.toUpperCase(),
      salesOrg: values.salesOrg
    };

    await updateCatalogType({ catalogTypeId, catalogTypeData });
    setSubmitting(false);
    resetForm();

    return fetchCatalogTypeById(catalogTypeId);
  };

  const handleDelete = async () => {
    await deleteCatalogType(selectedCatalogType.id);
    closeModal();

    return history.push('/catalogTypes');
  };

  const preventEnterKeySubmit = keyPress => {
    if ((keyPress.charCode || keyPress.keyCode) === 13) {
      keyPress.preventDefault();
    }
  };

  const body = () => {
    if (isFetching) {
      return <Spinner large />;
    }

    return (
      <Box pt='20px' px='30px' style={{ backgroundColor: 'white', marginTop: '0px' }}>
        <div style={{ width: '100%' }}>
          <Formik
            initialValues={selectedCatalogType || { name: '', salesOrg: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSave}
            enableReinitialize
          >
            {({ errors, handleBlur, handleChange, handleSubmit, touched, values, dirty }) => (
              <Form onSubmit={handleSubmit} onKeyDown={preventEnterKeySubmit}>
                <Flex alignItems='center'>
                  <TextFieldStyled
                    id='name'
                    error={errors.name && touched.name}
                    errorMessage={ERROR_MESSAGES.name}
                    label='Catalog Type Name'
                    name='name'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    inputProps={{
                      style: {
                        backgroundColor: 'white',
                        color: 'gray',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }
                    }}
                    style={{ width: '37.5%' }}
                  />
                  <Box mx={3} />
                  <DropDownStyled
                    id='salesOrg'
                    error={errors.salesOrg && touched.salesOrg}
                    errorMessage={ERROR_MESSAGES.salesOrg}
                    label='Sales Organization'
                    name='salesOrg'
                    onChange={handleChange}
                    options={SALES_ORGS}
                    value={values.salesOrg}
                    LabelProps={{ style: { zIndex: 10 } }}
                    style={{ width: '37.5%' }}
                  />
                  <Box mx={3} />
                  <ButtonStyled
                    disabled={!dirty || isUpdating}
                    type='submit'
                    appearance='primary'
                    background='light'
                    size='small'
                    style={{ marginBottom: '12px', width: '180px' }}
                  >
                    {isUpdating ? (
                      <span>
                        <Spinner small />
                      </span>
                    ) : (
                      <span>Apply Changes</span>
                    )}
                  </ButtonStyled>
                </Flex>
              </Form>
            )}
          </Formik>
          {!!selectedCatalogType && (
            <>
              <ModalStyled
                closeModal={closeModal}
                backdropCompProps={{ style: { backdropFilter: 'blur(4px)' } }}
                slots={{
                  FooterSlot: (
                    <ModalActionsStyled>
                      <ButtonStyled appearance='primary' onClick={handleDelete}>
                        Yes, Delete
                      </ButtonStyled>
                      <ButtonStyled appearance='secondary' onClick={closeModal}>
                        Cancel
                      </ButtonStyled>
                    </ModalActionsStyled>
                  )
                }}
                isOpen={isModalOpen}
                title='Are you sure?'
              >
                <p>Do you really want to delete &quot;{selectedCatalogType.name}&quot;?</p>
              </ModalStyled>
              <LinkedCatalogsAndAccounts catalogTypeId={selectedCatalogType.id} />
            </>
          )}
        </div>
      </Box>
    );
  };

  return (
    <Page
      alwaysShowHeader
      title='Edit Catalog Type'
      linkBack={{ url: '/catalogTypes', linkTitle: 'Catalog Types' }}
      noPadding
      noExtraPadding
      HeaderContent={
        !!selectedCatalogType && (
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '8px'
            }}
          >
            <strong style={{ fontWeight: 'bold', fontSize: '16px' }}>{selectedCatalogType.name}</strong>

            <ButtonStyled appearance='primary' onClick={openModal} type='button'>
              Delete
            </ButtonStyled>
          </div>
        )
      }
    >
      {body()}
    </Page>
  );
};

EditCatalogType.defaultProps = {
  selectedCatalogType: null
};

EditCatalogType.propTypes = {
  deleteCatalogType: func.isRequired,
  fetchCatalogTypeById: func.isRequired,
  history: object.isRequired,
  isFetching: bool.isRequired,
  selectedCatalogType: object,
  updateCatalogType: func.isRequired,
  fetchCatalogTypes: func.isRequired,
  isUpdating: bool.isRequired
};

const mapStateToProps = (state, ownProps) => {
  const catalogTypeId = ownProps?.match?.params?.catalogTypeId;

  return {
    isFetching: getPendingStatus(state, fetchCatalogTypes.event.name),
    isUpdating: getPendingStatus(state, updateCatalogType.event.name),
    selectedCatalogType: getCatalogTypeById(state, catalogTypeId)
  };
};

const mapDispatchToProps = {
  deleteCatalogType,
  fetchCatalogTypeById,
  fetchCatalogTypes,
  updateCatalogType
};

export default connect(mapStateToProps, mapDispatchToProps)(EditCatalogType);
